namespace $.$$ {

	type $shm_hitalama_map_rights_level = 'read' | 'write' | 'manage'
	type $shm_hitalama_map_rights_publication = {
		catalog: $shm_hitalama_map_access_catalog
		outbox: $shm_hitalama_map_access_outbox
		title: string
	} | null

	export class $shm_hitalama_map_rights extends $.$shm_hitalama_map_rights {
		private static readonly client_ids = new Map< string, string >

		map_type() {
			return $shm_hitalama_map.access_level_title( this.map().access_level() )
		}

		auto() {
			this.publication_repair_auto()
			return super.auto()
		}

		@ $mol_mem
		publication_repair_auto() {
			return this.$.$mol_error_fence( ()=> {
				return this.$.$shm_hitalama_profile.current()?.Map_access_outbox()?.remote()?.repair() ?? null
			}, ()=> null, ()=> null )
		}

		@ $mol_mem
		client_id( next?: string ) {
			const key = this.map().link().str
			if( next !== undefined ) {
				$shm_hitalama_map_rights.client_ids.delete( key )
				$shm_hitalama_map_rights.client_ids.set( key, next )
				if( $shm_hitalama_map_rights.client_ids.size > 256 ) {
					const oldest = $shm_hitalama_map_rights.client_ids.keys().next().value
					if( oldest ) $shm_hitalama_map_rights.client_ids.delete( oldest )
				}
			}
			return $shm_hitalama_map_rights.client_ids.get( key ) ?? ''
		}

		team_members() {
			return ( this.team() as $shm_hitalama_team | null )?.member_records() ?? []
		}

		team_member_options() {
			const owners = this.access_owner_ids()
			return Object.fromEntries([ [ '', 'Выберите участника' ] as const, ... this.team_members()
				.map( member => {
					const id = member.User_id()?.val()?.trim() ?? ''
					const name = member.Name()?.val()?.trim() || id
					return [ id, name === id ? id : `${ name } · ${ id }` ] as const
				} )
				.filter( ([ id ]) => this.client_id_valid_value( id ) && !owners.has( id ) )
				.toSorted( ( left, right )=> left[ 1 ].localeCompare( right[ 1 ], 'ru' ) ) ] )
		}

		rows() {
			const rows = super.rows()
			const accesses = this.access_ids()
			return rows.filter( row => {
				if( row === this.Team_member_label() ) return Object.keys( this.team_member_options() ).length > 1
				if( row === this.Access_empty() ) return accesses.length === 0
				if( row === this.Access_list() ) return accesses.length > 0
				return true
			} )
		}

		client_id_normalized() {
			return this.client_id().trim().replace( /^@/, '' )
		}

		client_id_valid_value( id: string ) {
			return /^[a-zA-Z0-9]{8}_[a-zA-Z0-9]{8}$/.test( id )
		}

		client_id_valid() {
			return this.client_id_valid_value( this.client_id_normalized() )
		}

		client_target_changeable() {
			return !this.access_owner_ids().has( this.client_id_normalized() )
		}

		can_manage() {
			const land = this.map().land()
			return land.lord_rank( land.auth().pass().lord() ) >= this.$.$giper_baza_rank_rule
		}

		grant_enabled() {
			return this.client_id_valid() && this.client_target_changeable() && this.can_manage()
		}

		@ $mol_action
		grant_read() {
			return this.grant( this.$.$giper_baza_rank_read, 'Права на чтение выданы' )
		}

		@ $mol_action
		grant_write() {
			return this.grant( this.$.$giper_baza_rank_post( 'just' ), 'Права на запись выданы' )
		}

		@ $mol_mem_key
		client_pass( id: string ) {
			const land = encodeURIComponent( this.map().land().link().str )
			const uri = `/hitalama_client_pass/${ encodeURIComponent( id ) }?land=${ land }`
			const text = this.$.$mol_fetch.text( uri ).trim()
			const pass = this.$.$giper_baza_auth_pass.from( text )
			if( pass.lord().str !== id ) $mol_fail( new Error( 'Client pass does not match client ID' ) )
			return pass
		}

		@ $mol_mem
		client_pass_registered() {
			const id = this.client_id_normalized()
			if( !this.client_id_valid() ) return null
			const lord = new this.$.$giper_baza_link( id )
			const team_pass = ( this.team() as $shm_hitalama_team | null )?.land().lord_pass( lord )
			if( team_pass ) return team_pass
			return this.$.$mol_error_fence(
				()=> this.client_pass( id ),
				()=> null,
				promise => $mol_fail_hidden( promise ),
			)
		}

		grant( rank: typeof $giper_baza_rank.Value, success: string ) {
			this.message( '' )
			if( !this.can_manage() ) return this.message( 'Недостаточно прав для управления картой' )

			const id = this.client_id_normalized()
			if( !this.client_id_valid() ) return this.message( 'Проверьте ID клиента' )
			const lord = new this.$.$giper_baza_link( id )
			const pass = this.map().land().lord_pass( lord ) ?? this.client_pass_registered()
			if( pass ) return this.grant_apply( pass, rank, success )

			this.grant_rank( rank )
			this.grant_success( success )
			this.grant_client( id )
			return this.message( 'Выдаём права…' )
		}

		@ $mol_action
		grant_apply( pass: $giper_baza_auth_pass, rank: typeof $giper_baza_rank.Value, success: string ) {
			const land = this.map().land()
			const publication = this.publication_prepare()
			// Public fallback readability is not a personal grant. Discovery must
			// have an explicit Gift even when the requested rank equals fallback.
			if( this.publication_intent( publication, pass, true, rank ) ) land.give( pass, rank )
			this.publication_enqueue( publication, pass, true )
			this.access_revision( this.access_revision() + 1 )
			this.grant_client( '' )
			return this.message( success )
		}

		@ $mol_action
		publication_prepare() {
			this.$.$shm_hitalama_baza_gift_pass_install()
			const catalog = this.$.$shm_hitalama_map_access_discovery.current().catalog()
			if( !catalog ) return null
			const profile = this.$.$shm_hitalama_profile.current()
			if( !profile ) $mol_fail( new Error( 'Для выдачи приглашения войдите в профиль' ) )
			const outbox = profile.map_access_outbox_ensure()
			return { catalog, outbox, title: this.map().title() }
		}

		@ $mol_action
		publication_intent( publication: $shm_hitalama_map_rights_publication,
			pass: $giper_baza_auth_pass, active: boolean, rank: typeof $giper_baza_rank.Value, always_change = false ) {
			const land = this.map().land()
			return publication
				? publication.outbox.prepare( publication.catalog, land, pass, active, publication.title, rank, always_change )
				: always_change || land._gift.get( pass.lord().str )?.rank() !== rank
		}

		@ $mol_action
		publication_enqueue( publication: $shm_hitalama_map_rights_publication,
			pass: $giper_baza_auth_pass, active: boolean ) {
			this.$.$shm_hitalama_map_access_grant.record( this.map().land(), pass.lord().str, active )
			if( !publication ) return
			publication.outbox.enqueue( publication.catalog, this.map().land(), pass, active, publication.title )
		}

		grant_pending_auto() {
			const id = this.grant_client()
			if( !id ) return

			const lord = new this.$.$giper_baza_link( id )
			const known = this.map().land().lord_pass( lord )
				?? ( this.team() as $shm_hitalama_team | null )?.land().lord_pass( lord )
			const published = known ?? this.$.$giper_baza_glob.Land( lord ).sync().lord_pass( lord )
			const pass = published ?? this.client_pass_registered()
			if( !pass ) return

			return this.grant_apply(
				pass,
				this.grant_rank() as typeof $giper_baza_rank.Value,
				this.grant_success(),
			)
		}

		access_base_rank() {
			return this.map().land().lord_rank( null )
		}

		access_owner_ids() {
			const land = this.map().land()
			const king = land.link().lord().str
			const owners = new Set([ king ])
			const gifts = ( land as any )._gift as Map< string, $giper_baza_unit_gift > | undefined
			for( const gift of gifts?.values() ?? [] ) {
				if( gift.lord().str !== king ) continue
				if( gift.rank() < this.$.$giper_baza_rank_rule ) continue
				if( gift.mate().str ) owners.add( gift.mate().str )
			}
			return owners
		}

		access_ids() {
			this.access_revision()
			const land = this.map().land()
			const owners = this.access_owner_ids()
			const base = this.access_base_rank()
			const gifts = ( land as any )._gift as Map< string, $giper_baza_unit_gift > | undefined
			return [ ... ( gifts?.keys() ?? [] ) ]
				.filter( id => id && !owners.has( id ) && (
					land.lord_rank( new this.$.$giper_baza_link( id ) ) !== base
					|| this.$.$shm_hitalama_map_access_grant.status( land, id ) === true
				) )
				.toSorted( ( left, right )=> this.access_name( left ).localeCompare( this.access_name( right ), 'ru' ) )
		}

		access_rows() {
			return this.access_ids().map( id => this.Access_row( id ) )
		}

		access_member( id: string ) {
			return ( this.team() as $shm_hitalama_team | null )?.member_by_user( id ) ?? null
		}

		access_name( id: string ) {
			const member = this.access_member( id )
			const member_name = member?.Name()?.val()?.trim()
			if( member_name ) return member_name
			return this.$.$mol_error_fence(
				()=> this.$.$shm_hitalama_profile.user_name( id ) || id,
				()=> id,
				()=> id,
			)
		}

		access_avatar_uri( id: string ) {
			return this.$.$mol_error_fence(
				()=> this.$.$shm_hitalama_profile.user_avatar_uri( id ),
				()=> '',
				()=> '',
			)
		}

		access_id( id: string ) {
			return id
		}

		access_rank_options() {
			return {
				read: 'Чтение',
				write: 'Редактирование',
				manage: 'Управление правами',
			} as Record< $shm_hitalama_map_rights_level, string >
		}

		access_rank_value( rank: typeof $giper_baza_rank.Value ): $shm_hitalama_map_rights_level {
			const tier = this.$.$giper_baza_rank_tier_of( rank )
			if( tier >= this.$.$giper_baza_rank_tier.rule ) return 'manage'
			if( tier >= this.$.$giper_baza_rank_tier.post ) return 'write'
			return 'read'
		}

		access_rank_number( level: $shm_hitalama_map_rights_level ) {
			if( level === 'manage' ) return this.$.$giper_baza_rank_rule
			if( level === 'write' ) return this.$.$giper_baza_rank_post( 'just' )
			return this.$.$giper_baza_rank_read
		}

		@ $mol_mem_key
		access_rank( id: string, next?: string ) {
			const land = this.map().land()
			const lord = new this.$.$giper_baza_link( id )
			const current = this.access_rank_value( land.lord_rank( lord ) )
			if( next === undefined ) return current
			if( !this.access_change_enabled( id ) ) return current
			if( !( next in this.access_rank_options() ) ) return current

			const pass = land.lord_pass( lord )
				?? ( this.team() as $shm_hitalama_team | null )?.land().lord_pass( lord )
			if( !pass ) {
				this.message( `Не найден публичный ключ пользователя ${ id }` )
				return current
			}

			const rank = this.access_rank_number( next as $shm_hitalama_map_rights_level )
			this.access_change_apply( pass, rank )
			this.access_revision( this.access_revision() + 1 )
			this.message( 'Права изменены' )
			return next
		}

		@ $mol_action
		access_change_apply( pass: $giper_baza_auth_pass, rank: typeof $giper_baza_rank.Value ) {
			const publication = this.publication_prepare()
			const land = this.map().land()
			if( this.publication_intent( publication, pass, true, rank ) ) land.give( pass, rank )
			this.publication_enqueue( publication, pass, true )
		}

		access_change_enabled( id: string ) {
			return this.can_manage() && !this.access_owner_ids().has( id )
		}

		@ $mol_action
		access_revoke( id: string, next?: Event ) {
			if( next === undefined || !this.access_change_enabled( id ) ) return null
			const land = this.map().land()
			const lord = new this.$.$giper_baza_link( id )
			const pass = land.lord_pass( lord )
				?? ( this.team() as $shm_hitalama_team | null )?.land().lord_pass( lord )
			if( !pass ) {
				this.message( `Не найден публичный ключ пользователя ${ id }` )
				return next
			}
			const base = this.access_base_rank()
			const publication = this.publication_prepare()
			// Advance the Gift even for public-read -> public-read revocation:
			// invitations from other administrators must expire as well.
			this.publication_intent( publication, pass, false, base, true )
			land.give( pass, base )
			this.publication_enqueue( publication, pass, false )
			this.access_revision( this.access_revision() + 1 )
			this.message( 'Индивидуальные права отозваны' )
			return next
		}

	}

}
